import type {
  CameraMovementFeedbackKind,
  WinchMovementFeedbackKind,
} from './cameraMovementFeedback.types.ts';

const WINCH_MOVEMENT_KINDS = [
  'winchUp',
  'winchDown',
  'winchIn',
  'winchOut',
] as const satisfies readonly WinchMovementFeedbackKind[];

export const isWinchMovementFeedbackKind = (
  kind: CameraMovementFeedbackKind
): kind is WinchMovementFeedbackKind =>
  (WINCH_MOVEMENT_KINDS as readonly CameraMovementFeedbackKind[]).includes(kind);

/** Picks pan vs tilt (or winch horizontal vs vertical) from the dominant non-zero axis. */
const dominantDirection = (
  horizontal: number,
  vertical: number
): 'horizontal' | 'vertical' | null => {
  const absH = Math.abs(horizontal);
  const absV = Math.abs(vertical);
  if (absH === 0 && absV === 0) return null;
  // Equal magnitudes prefer horizontal (typical diagonal gamepad input).
  return absH >= absV ? 'horizontal' : 'vertical';
};

/** Maps pan-tilt speeds to overlay feedback kind (positive tilt = up, same as keyboard). */
export const getMovementFeedbackFromPanTilt = (
  pan: number,
  tilt: number
): CameraMovementFeedbackKind | null => {
  const axis = dominantDirection(pan, tilt);
  if (axis === 'horizontal') return pan > 0 ? 'panRight' : 'panLeft';
  if (axis === 'vertical') return tilt > 0 ? 'tiltUp' : 'tiltDown';
  return null;
};

/** Maps 2D winch speeds to overlay feedback kind (positive vertical = up). */
export const getMovementFeedbackFromWinch2D = (
  horizontalSpeed: number,
  verticalSpeed: number
): CameraMovementFeedbackKind | null => {
  const axis = dominantDirection(horizontalSpeed, verticalSpeed);
  if (axis === 'horizontal') return horizontalSpeed > 0 ? 'winchOut' : 'winchIn';
  if (axis === 'vertical') return verticalSpeed > 0 ? 'winchUp' : 'winchDown';
  return null;
};

/** TanStack `mutate` options: trigger stream overlay only after a successful POST. */
export const movementFeedbackMutateOptions = (
  onMovementSuccess: ((kind: CameraMovementFeedbackKind) => void) | undefined,
  kind: CameraMovementFeedbackKind
) => (onMovementSuccess ? { onSuccess: () => onMovementSuccess(kind) } : undefined);
